import React from 'react';
import PropTypes from 'prop-types';
import {locationShape, routerShape, withRouter} from 'react-router';
import {observable, autorun} from 'mobx';
import {inject, observer} from 'mobx-react';
import {suspectInfiniteLoop} from 'spa/debug';
// UNCOMMENT TO USE SPLIT IO AGAIN
import { SplitSdk, SplitFactory } from '@splitsoftware/splitio-react';
import ClientSplitDestroy from 'spa/Components/SplitIo/ClientSplitDestroy';
import CookieBanner from 'spa/Components/CookieBanner';
import DocumentTitle from 'spa/Components/DocumentTitle';
import DocumentMeta from 'spa/Components/DocumentMeta';
import SmartBanner from 'spa/Components/SmartBanner';
import SessionSplit from 'spa/Components/SessionSplit';
import Notifications from 'spa/Components/Notifications';
import {withI18n} from 'spa/Components/Context';
import Cookies from 'js-cookie';

const DISMISSED_COOKIE_BANNER_COOKIE_NAME = 'CookieConsent';

const isServer = typeof window === 'undefined';

const {
  HTTP_HOST,
  SPLIT_IO_API_BROWSER_KEY,
} = isServer ? {
  HTTP_HOST: process.env.HTTP_HOST,
  SPLIT_IO_API_BROWSER_KEY: process.env.SPLIT_IO_API_BROWSER_KEY,
} : {
  HTTP_HOST: window.HTTP_HOST,
  SPLIT_IO_API_BROWSER_KEY: window.SPLIT_IO_API_BROWSER_KEY,
};

@inject('store')
@withRouter
@observer
@withI18n
class App extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    location: locationShape.isRequired,
    router: routerShape.isRequired,
    routes: PropTypes.array.isRequired,
    store: PropTypes.object.isRequired,
  };

  UNSAFE_componentWillMount() {
    const {i18n} = this.context;
    const {router, store} = this.props;
    const {session, umDeviceIdentifier} = store;
    const pathname = this.props.location && this.props.location.pathname;

    this.generateMeta();
    this.showNotifications();

    if (pathname.indexOf('account-blocked') === -1 && session && session.user && session.user.accountBlocked) {
      return router.push(`/${i18n.lc}/account-blocked`);
    }

    // WEB-1160 - hiding the smart banner on landing-pages with /lp4/ in the URL as an experiment (split test done at an adwords level)
    if (pathname.indexOf('/lp4/') >= 0) {
      this.showSmartBanner = false;
    } else {
      this.showSmartBanner = true;
    }

    this.isCookieBannerDismissed = Cookies.get(DISMISSED_COOKIE_BANNER_COOKIE_NAME);

    this.key = umDeviceIdentifier;
    if (session.user) {
      this.key = session.user.id;
    }

    // UNCOMMENT TO USE SPLIT IO AGAIN
    if (!isServer) {
      this.splitFactory = SplitSdk({
        core: {
          authorizationKey: SPLIT_IO_API_BROWSER_KEY,
          key: `${this.key}`,
        },
      });
    }

    // The category-pre-launch is to be shown as a webview in the apps, so we don't want the cookie
    // banner showing
    // checkout.com 3ds iframe redirects shouldn't show the cookie banner
    if (
      pathname.indexOf('/category-pre-launch/') >= 0 ||
      pathname.indexOf('/checkout-gateway-3ds-success-redirect') >= 0 ||
      pathname.indexOf('/checkout-gateway-3ds-failure-redirect') >= 0
    ) {
      this.canShowCookieBanner = false;
      this.showSmartBanner = false;
    } else {
      this.canShowCookieBanner = true;
    }
  }

  componentDidMount() {
    this.listeners = [
      autorun(() => {
        const {session} = this.props.store;
        const {user} = session;

        if (!user) {
          const {
            userPaymentMethods,
            orders,
            userAddresses,
            home,
          } = this.props.store;

          if (userPaymentMethods) userPaymentMethods.reset();

          if (orders.ordersGet && orders.ordersGet.searches) {
            orders.ordersGet.searches.clear();
          }

          if (userAddresses) userAddresses.reset();

          if (home && home.searches) home.searches.clear();
        }
      }, {
        name: 'reset session on logout',
      }),
    ];
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {i18n} = this.context;
    const {router, store} = this.props;
    const {session} = store;

    if (nextProps.location !== this.props.location) {
      const {pathname} = nextProps.location;

      if (
        pathname.indexOf('account-blocked') === -1 &&
        session &&
        session.user &&
        ((session.user.meta &&
        session.user.meta.block) || session.user.accountBlocked)
      ) {
        return router.push(`/${i18n.lc}/account-blocked`);
      }

      this.showNotifications(nextProps);
    }
    if (
      nextProps.location !== this.props.location ||
      nextProps.routes !== this.props.routes
    ) {
      this.generateMeta(nextProps);
    }
  }

  componentWillUnmount() {
    while (this.listeners && this.listeners.length) {
      this.listeners.pop()();
    }
  }

  generateMeta({router, location, routes} = this.props) {
    const {i18n} = this.context;

    const canonicalQueryArgs = routes
      .map(route => route.canonicalQueryArgs)
      .reduce((a, b) => a.concat(b), [])
      .filter(Boolean);

    const query = canonicalQueryArgs.reduce((query, arg) => {
      query[arg] = location.query[arg];
      return query;
    }, {});

    this.canonical = router.createHref({...location, query});
    //TODO: better use static html file to not load react library for webview
    this.isAppIframe = routes
      .reduce((isAppIframe, route) => route.isAppIframe || isAppIframe, false);

    const fullUrl = this.canonical.split('?');
    const newUrl = fullUrl[0].split('/');

    if (newUrl.length > 3 && newUrl[newUrl.length - 1] === '') {
      this.canonical = this.canonical.slice(0, -1);
    }

    if (i18n.lc && this.canonical.indexOf(i18n.lc) !== -1) {
      const supportedLocales = routes
        .reduce((locales, route) => route.supportedLocales || locales, i18n.SUPPORTED_LOCALES)
        .map(locale => locale.toLowerCase());
      this.alternateLinks = supportedLocales.map(locale => ({
        href: this.canonical.replace(i18n.lc, locale),
        hrefLang: locale,
      }));

      const defaults = i18n.DEFAULT_LOCALES.map(locale => (
        locale.toLowerCase()
      ));
      // We also need to include default URL's for language only so en -> en-gb, fr -> fr-fr etc
      const defaultLocales = supportedLocales.filter(locale => (
        defaults.indexOf(locale) !== -1
      )).map(item => ({
        href: this.canonical.replace(i18n.lc, item),
        hrefLang: item.split('-')[0],
      }));

      this.alternateLinks = this.alternateLinks.concat(defaultLocales);

      this.alternateLinks.forEach((alt) => {
        if (alt.href && !/http/.test(alt.href)) {
          alt.href = `https://${HTTP_HOST}${alt.href}`;
        }
      });
    } else {
      this.alternateLinks = [];
    }
  }

  @suspectInfiniteLoop
  showNotifications(props = this.props) {
    const {i18n} = this.context;
    if (props.location.query) {
      switch (props.location.query.message) {
      case 'alreadyloggedin':
        props.store.notifications.add({
          id: 'login',
          type: 'success',
          title: i18n.__('You\'re already logged in!'),
        });
        break;
      case 'loggedin':
        props.store.notifications.add({
          id: 'login',
          type: 'success',
          title: i18n.__('You\'ve been logged in!'),
        });
        break;
      case 'signedup':
        props.store.notifications.add({
          id: 'login',
          type: 'success',
          title: i18n.__('Your account has been created successfully!'),
        });
        break;
      default: return;
      }

      const href = props.router.createHref({
        ...props.location,
        query: {
          ...props.location.query,
          message: undefined,
        },
      });

      props.router.replace(href);
    }
  }

  @observable isCookieBannerDismissed = false;
  @observable showSmartBanner = false;
  @observable splitFactory;

  render() {
    const {children} = this.props;
    const {i18n} = this.context;

    // UNCOMMENT TO USE SPLIT IO AGAIN
    const content = isServer ? (
      <div className="full-height">
        <Notifications key="notifications" />
        {children}
      </div>
    ) : (
      <SplitFactory factory={this.splitFactory}>
        <ClientSplitDestroy splitUserKey={this.key}>
          <div className="full-height">
            <Notifications key="notifications" />
            {children}
          </div>
        </ClientSplitDestroy>
      </SplitFactory>
    );

    // UNCOMMENT THIS WHEN DISABLING SPLIT IO
    // const content = (
    //   <div className="full-height">
    //     <Notifications key="notifications" />
    //     {children}
    //   </div>
    // );

    return (
      <DocumentTitle title={i18n._x('Urban', 'site title')}>
        <DocumentMeta
          viewport="width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
          title={i18n._x('Mobile Wellness - Urban', 'Default open graph title')}
          meta={{
            httpEquiv: {'X-UA-Compatible': 'IE=edge'},
            property: {
              'og:site_name': 'Urban',
              'og:image': 'https://res.cloudinary.com/huxr6hrje/image/upload/f_auto,c_fill,h_630,w_1200/v1542384025/b2c-art/og-images/fb_share_2x.png',
              'business:contact_data:locality': 'London',
              'business:contact_data:country_name': 'United Kingdom',
            },
            name: {
              'twitter:card': 'summary',
              'twitter:site': '@Urbanappuk',
              'twitter:creator': '@Urbanappuk',
              'twitter:image': 'https://res.cloudinary.com/huxr6hrje/image/upload/f_auto,c_fill,h_1500,w_1500/v1542384025/b2c-art/og-images/fb_share_2x.png',
              'twitter:image:alt': i18n._x('Woman in gym kit with Urban logo', 'Alternate open graph image text'),
            },
          }}
          canonical={this.canonical}
          link={{
            rel: {
              alternate: this.alternateLinks,
            },
          }}
          auto={{
            ograph: true,
            twitter: true,
          }}
        >
          {this.showSmartBanner && !this.isAppIframe && <SmartBanner />}
          <SessionSplit key="session-split">
            {content}
          </SessionSplit>
          {(this.canShowCookieBanner && !this.isAppIframe && !this.isCookieBannerDismissed) &&
            <CookieBanner
              cookieName={DISMISSED_COOKIE_BANNER_COOKIE_NAME}
              key="cookie-banner"
            />
          }
        </DocumentMeta>
      </DocumentTitle>
    );
  }
}

export default App;



// WEBPACK FOOTER //
// ./spa/Pages/App.jsx